import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const MENU_GAP = 6;
const VIEW_PAD = 8;

const placeMenu = (anchor, menu) => {
  const width = Math.max(menu.offsetWidth, anchor.width, 168);
  const height = menu.offsetHeight;
  let left = anchor.left;
  let top = anchor.bottom + MENU_GAP;
  if (left + width > window.innerWidth - VIEW_PAD) {
    left = window.innerWidth - VIEW_PAD - width;
  }
  if (left < VIEW_PAD) left = VIEW_PAD;
  if (top + height > window.innerHeight - VIEW_PAD) {
    top = anchor.top - height - MENU_GAP;
  }
  if (top < VIEW_PAD) top = VIEW_PAD;
  return { left, top, width };
};

/**
 * Scope picker. The closed control stays a quiet label; the list is our own
 * menu so items can have real padding. Native selects ignore that on Windows.
 */
const ScopeSelect = ({ value, options, label, onChange, onOpenChange }) => {
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);

  const setOpenState = (next) => {
    setOpen(next);
    onOpenChange?.(next);
  };

  useLayoutEffect(() => {
    if (!open) return undefined;
    const anchor = triggerRef.current?.getBoundingClientRect();
    const menu = menuRef.current;
    if (!anchor || !menu) return undefined;
    setPos(placeMenu(anchor, menu));
    return undefined;
  }, [open, options, value]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (e.target.closest?.('.variables-panel__scope-menu')) return;
      setOpenState(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpenState(false);
    };
    const close = () => setOpenState(false);
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const toggle = () => {
    if (open) {
      setOpenState(false);
      return;
    }
    setPos(null);
    setOpenState(true);
  };

  const pick = (next) => {
    setOpenState(false);
    if (next !== value) onChange?.(next);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`variables-panel__scope-trigger${open ? ' is-open' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        onMouseDown={(e) => e.preventDefault()}
        onClick={toggle}
      >
        <span className="variables-panel__scope-label">{label}</span>
        <svg
          className="variables-panel__scope-chevron"
          width="9"
          height="6"
          viewBox="0 0 9 6"
          aria-hidden="true"
        >
          <path
            d="M1 1.25L4.5 4.7 8 1.25"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.25"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            className="variables-panel__scope-menu"
            role="listbox"
            aria-label={label}
            style={
              pos
                ? { left: pos.left, top: pos.top, width: pos.width }
                : { left: -9999, top: 0 }
            }
          >
            {options.map((opt) => {
              const selected = opt.value === value;
              const tone =
                opt.value === 'doc' || opt.value === 'mission' || opt.value === 'campaign'
                  ? opt.value
                  : 'campaign';
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`variables-panel__scope-menu-item${selected ? ' is-selected' : ''}`}
                  data-scope={tone}
                  onClick={() => pick(opt.value)}
                >
                  <span className="variables-panel__scope-dot" aria-hidden="true" />
                  <span className="variables-panel__scope-menu-label">{opt.label}</span>
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
};

export default ScopeSelect;
